/**
 * Pure helpers for the `rank` survey question type.
 *
 * The stored response for a rank question is an array, slot order, of the
 * ranked option `value`s ('' for an empty slot). At render time it is more
 * convenient to work with per-slot option indices, so these helpers convert
 * between the two representations and compute the result of a drag-and-drop.
 */

/** A single rank option: a stored `value` and a user-facing `label`. */
export interface RankOption {
  value: string;
  label: string;
}

/** Where a dragged option was dropped: back into the bank, or onto a slot. */
export type RankDropTarget = {type: 'bank'} | {type: 'slot'; slot: number};

/**
 * Maps a stored ranked-value array onto per-slot option indices.
 *
 * @param value - The raw stored value; expected to be an array, slot order, of
 *   option `value`s ('' for an empty slot). Any non-array (e.g. the initial
 *   empty-string Formik default) is treated as "nothing ranked yet".
 * @param options - The full option list for this question.
 * @return An array with one entry per slot: the index into `options` ranked in
 *   that slot, or `null` when the slot is empty. Duplicate or unknown stored
 *   values are ignored so an option never appears in two slots.
 */
export const rankValuesToSlotIndices = (
  value: unknown,
  options: RankOption[],
): (number | null)[] => {
  const slots: (number | null)[] = options.map(() => null);
  if (!Array.isArray(value)) return slots;
  const usedOptions = new Set<number>();
  for (let slot = 0; slot < options.length; slot += 1) {
    const stored = value[slot];
    if (typeof stored !== 'string' || stored === '') continue;
    const optionIndex = options.findIndex(
      (option, index) => option.value === stored && !usedOptions.has(index),
    );
    if (optionIndex !== -1) {
      slots[slot] = optionIndex;
      usedOptions.add(optionIndex);
    }
  }
  return slots;
};

/**
 * Inverse of {@link rankValuesToSlotIndices}: turns per-slot option indices
 * back into the stored ranked-value array.
 *
 * @param slots - Per-slot option indices (`null` for an empty slot).
 * @param options - The full option list for this question.
 * @return An array, slot order, of option `value`s ('' for an empty slot).
 */
export const slotIndicesToRankValues = (
  slots: (number | null)[],
  options: RankOption[],
): string[] =>
  slots.map(optionIndex =>
    optionIndex === null ? '' : options[optionIndex].value,
  );

/**
 * Reports whether nothing has been ranked yet.
 *
 * @param value - The raw stored value; expected to be an array, slot order, of
 *   option `value`s ('' for an empty slot). A non-array (e.g. the initial
 *   Formik default) counts as empty.
 * @return `true` when `value` is a non-array or an array whose entries are all
 *   empty strings; `false` if any slot holds an option.
 */
export const isRankEmpty = (value: unknown): boolean => {
  if (!Array.isArray(value)) return true;
  return value.every(entry => typeof entry !== 'string' || entry === '');
};

/**
 * Reports whether every option has been ranked.
 *
 * @param value - The raw stored value; expected to be an array, slot order, of
 *   option `value`s ('' for an empty slot). Any non-array is treated as
 *   incomplete.
 * @param optionCount - The number of options in the question.
 * @return `true` only when `value` is an array with exactly `optionCount`
 *   entries and none of them is empty; `false` otherwise.
 */
export const isRankComplete = (
  value: unknown,
  optionCount: number,
): boolean => {
  if (!Array.isArray(value) || value.length !== optionCount) return false;
  return value.every(entry => typeof entry === 'string' && entry !== '');
};

/**
 * Computes the new per-slot layout after dropping an option.
 *
 * Dropping onto the bank removes the option from its current slot (if any).
 * Dropping onto an occupied slot swaps the two options when the dragged option
 * came from another slot; if it came from the bank, the previous occupant is
 * displaced back to the bank.
 *
 * @param slotIndices - The current per-slot option indices (`null` for empty).
 * @param optionIndex - The index (into the option list) of the dragged option.
 * @param target - Where the option was dropped (bank or a specific slot).
 * @return A new per-slot array; the input is not mutated.
 */
export const computeRankDrop = ({
  slotIndices,
  optionIndex,
  target,
}: {
  slotIndices: (number | null)[];
  optionIndex: number;
  target: RankDropTarget;
}): (number | null)[] => {
  const next = [...slotIndices];
  const fromSlot = next.indexOf(optionIndex);

  if (target.type === 'bank') {
    if (fromSlot !== -1) next[fromSlot] = null;
    return next;
  }

  const toSlot = target.slot;
  const displaced = next[toSlot];
  if (fromSlot !== -1) next[fromSlot] = null;
  next[toSlot] = optionIndex;
  if (displaced !== null && displaced !== optionIndex && fromSlot !== -1) {
    next[fromSlot] = displaced;
  }
  return next;
};
